import React, { useEffect, useState } from 'react';
import { Menu, X, User, Zap, Shield } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LiveScanTicker } from './LiveScanTicker';
import { trackCtaClick } from '../../utils/visitorTracking';
export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const {
    user
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleNavClick = (sectionId: string) => {
    setIsMenuOpen(false);
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/');
      // Use setTimeout to allow navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };
  return <>
      <LiveScanTicker data-id="element-2547" data-mp="1xic7iycdh4n3" />
      <header className={`sticky top-0 z-50 transition-all duration-300 border-b ${scrolled ? 'bg-[#050505]/95 backdrop-blur-md border-gray-800 py-3' : 'bg-[#050505] border-transparent py-5'}`} data-id="element-2548" data-mp="1xgsrn8umovlu">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between" data-id="element-2549" data-mp="26is8a01qdxo9">
          <Link to="/" className="flex items-center gap-3 group" data-id="element-2550" data-mp="h43vvyt54fq8">
            <img src="https://cdn.magicpatterns.com/uploads/65popPutPXjwqKGt3qtuH1/flag.jpg" alt="ToxicDetector logo" className="w-8 h-8 object-contain" data-id="element-2551" data-mp="c4razjltxiqa" />
            <div className="flex flex-col" data-id="element-2552" data-mp="2h4sci05hz5cw">
              <h1 className="text-lg font-black text-white uppercase tracking-tight leading-none group-hover:text-[#FF0000] transition-colors" data-id="element-2553" data-mp="2h5u0143p9kbi">
                ToxicDetector
              </h1>
              <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest hidden sm:block" data-id="element-2554" data-mp="2q7jrg9xl0j9a">
                Forensic Analysis Unit
              </span>
            </div>
          </Link>

          {/* Mobile: Profile + Sign In + Menu */}
          <div className="md:hidden flex items-center gap-4" data-id="element-2555" data-mp="34umqwm5gt1z9">
            {user && <Link to="/profile" className="text-gray-400 hover:text-[#FFD700] transition-colors" aria-label="User profile" data-id="element-2556" data-mp="38whzmbpq6hn2">
                <User size={20} data-id="element-2557" data-mp="3iuxi5qaosiky" />
              </Link>}
            <Link to="/login" className="text-xs font-bold text-gray-300 hover:text-white uppercase tracking-wider transition-colors" data-id="element-2558" data-mp="3iv76l9g32hoj">
              Login
            </Link>
            <button className="text-white hover:text-[#FF0000] transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-2559" data-mp="3o248z54ovq1g">
              {isMenuOpen ? <X size={24} data-id="element-2560" data-mp="fjomuw71294t" /> : <Menu size={24} data-id="element-2561" data-mp="fk7spejel72h" />}
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1" data-id="element-2562" data-mp="akli7cuym4xd">
            {['Features', 'Why Us', 'Pricing', 'FAQ'].map(item => <button key={item} onClick={() => handleNavClick(item.toLowerCase().replace(' ', '-') === 'why-us' ? 'comparison' : item.toLowerCase())} className="px-4 py-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg font-bold text-xs uppercase tracking-widest transition-all" data-id="element-2563" data-mp="5sbv04l8biys">
                {item}
              </button>)}

            <a href="/about" className="px-4 py-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg font-bold text-xs uppercase tracking-widest transition-all" data-id="element-2564" data-mp="51b090m5kj92">
              About Us
            </a>

            <div className="w-px h-6 bg-gray-800 mx-4" data-id="element-2565" data-mp="wsqy5qt5tsel" />

            <Link to="/login" className="px-4 py-2 text-xs font-bold text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors uppercase tracking-widest mr-2" data-id="element-2566" data-mp="11rtrwf3owc79">
              Login
            </Link>

            {/* Profile Icon - Only show for authenticated users */}
            {user && <Link to="/profile" className="p-2 text-gray-400 hover:text-[#FFD700] hover:bg-white/5 rounded-lg transition-colors mr-2" aria-label="User profile" data-id="element-2567" data-mp="1fps4beyg9yk4">
                <User size={18} data-id="element-2568" data-mp="3093wz7lbc31f" />
              </Link>}

            <Link to="/signup" onClick={() => trackCtaClick('Start Scan', 'header', '/signup')} className="group relative bg-[#FF0000] text-white px-6 py-2.5 font-black text-xs uppercase tracking-widest hover:bg-red-600 transition-all shadow-[0_0_15px_rgba(255,0,0,0.4)] hover:shadow-[0_0_25px_rgba(255,0,0,0.6)] overflow-hidden skew-x-[-10deg]" data-id="element-2569" data-mp="308u85nc081xb">
              <div className="skew-x-[10deg] flex items-center gap-2" data-id="element-2570" data-mp="2hc2ubtf8ef0n">
                <Zap size={14} fill="currentColor" data-id="element-2571" data-mp="2w8aaminiqwan" />
                <span data-id="element-2572" data-mp="2r8flajje6erp">Start Scan</span>
              </div>
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 skew-x-[10deg]" data-id="element-2573" data-mp="2r6upyrp96rss" />
            </Link>
          </nav>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden absolute top-full left-0 w-full bg-[#050505] border-b border-gray-800 overflow-hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-2574" data-mp="6kjcpkpe3b6h">
          <nav className="px-6 py-6 space-y-2" data-id="element-2575" data-mp="nkma8y0ydbsd">
            {['Features', 'Why Us', 'Pricing', 'FAQ'].map(item => <button key={item} onClick={() => handleNavClick(item.toLowerCase().replace(' ', '-') === 'why-us' ? 'comparison' : item.toLowerCase())} className="block w-full text-left px-4 py-3 text-gray-400 hover:bg-white/5 hover:text-white rounded-lg font-bold text-sm uppercase tracking-wider transition-colors" data-id="element-2576" data-mp="si5nbulwziiq">
                {item}
              </button>)}
            <a href="/about" onClick={() => setIsMenuOpen(false)} className="block w-full text-left px-4 py-3 text-gray-400 hover:bg-white/5 hover:text-white rounded-lg font-bold text-sm uppercase tracking-wider transition-colors" data-id="element-2577" data-mp="3d8d7dtr9csvh">
              About Us
            </a>

            <div className="pt-4 space-y-3 border-t border-gray-800 mt-4" data-id="element-2578" data-mp="1lbskc6kjhj68">
              {user && <Link to="/profile" className="flex items-center justify-center gap-2 w-full px-4 py-3 text-gray-300 hover:bg-white/5 rounded-lg font-bold text-sm uppercase tracking-wider transition-colors border border-gray-800" onClick={() => setIsMenuOpen(false)} data-id="element-2579" data-mp="1gdhcu2hpvkr6">
                  <User size={16} data-id="element-2580" data-mp="xnhsfswtmtu3" />
                  <span data-id="element-2581" data-mp="xoatcd93mp4b">My Profile</span>
                </Link>}
              <Link to="/signup" className="block w-full bg-[#FF0000] text-white text-center px-4 py-3 font-black text-sm uppercase tracking-widest hover:bg-red-600 transition-all shadow-[0_0_15px_rgba(255,0,0,0.3)]" onClick={() => {
              trackCtaClick('Start Forensic Scan', 'header_mobile_menu', '/signup');
              setIsMenuOpen(false);
            }} data-id="element-2582" data-mp="so55z99v3os1">
                Start Forensic Scan
              </Link>
            </div>
          </nav>
        </div>
      </header>
    </>;
};